import React from 'react';
import { Avatar, Typography, Tag } from 'antd';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { useUserData } from '../../../hooks/useUsers';
import { setCurrentChat } from '../../../redux/currentChat/slice';
import { HeartOutlined, ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { auth } from '../../../firebaseConfig';

const { Text } = Typography;

const ConversationPreview = ({ conversation, activeTab }) => {
  const { userData } = useUserData(conversation[1].userInfo.id);
  const dispatch = useAppDispatch();
  const currentChatID = useAppSelector((state) => state.currentChat.chatID);
  const isActive = currentChatID === conversation[1].userInfo.id;
  const isUnread = conversation[1].unread && activeTab === 'inbox';
  
  const currentUserId = auth.currentUser?.uid;
  const lastMessage = conversation[1].lastMessage;
  const isSentByMe = lastMessage?.senderID === currentUserId;

  function handleSelectConversation() {
    dispatch(setCurrentChat(conversation[1].userInfo.id));
  }

  const lastMessageDate = new Date(conversation[1].date?.seconds * 1000);
  const isToday = new Date().toDateString() === lastMessageDate.toDateString();
  const timeString = isToday 
    ? lastMessageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : lastMessageDate.toLocaleDateString();

  // Show different message preview based on tab
  const getMessagePreview = () => {
    if (!lastMessage?.text) return 'No messages yet';
    
    switch (activeTab) {
      case 'inbox':
        return lastMessage.text;
      case 'sent':
        return `You: ${lastMessage.text}`;
      default:
        return lastMessage.text;
    }
  };

  const getMessageIcon = () => {
    switch (activeTab) {
      case 'sent':
        return <ArrowRightOutlined style={{ fontSize: '12px', color: '#667eea' }} />;
      case 'inbox':
        return <ArrowLeftOutlined style={{ fontSize: '12px', color: '#52c41a' }} />;
      default:
        return null;
    }
  };

  return (
    <div
      onClick={handleSelectConversation}
      style={{
        padding: '16px 20px',
        cursor: 'pointer',
        borderLeft: isActive ? '4px solid #667eea' : '4px solid transparent',
        background: isActive 
          ? 'linear-gradient(90deg, rgba(102, 126, 234, 0.1) 0%, rgba(255, 255, 255, 0.8) 100%)'
          : isUnread
          ? 'linear-gradient(90deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 255, 255, 0.5) 100%)'
          : 'transparent',
        transition: 'all 0.2s ease',
        borderBottom: '1px solid rgba(0, 0, 0, 0.04)'
      }}
      onMouseOver={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)';
        }
      }}
      onMouseOut={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = isUnread 
            ? 'linear-gradient(90deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 255, 255, 0.5) 100%)'
            : 'transparent';
        }
      }}
    >
      <div style={{ 
        display: 'flex', 
        gap: '12px'
      }}>
        {/* Avatar */}
        <Avatar 
          size={52}
          src={userData?.profileImages?.[0]?.url || `https://api.dicebear.com/7.x/miniavs/svg?seed=${userData?.uid}`}
          style={{ 
            border: '3px solid rgba(102, 126, 234, 0.15)',
            flexShrink: 0
          }}
        />
        
        {/* Content */}
        <div style={{ 
          flex: 1, 
          minWidth: 0,
          overflow: 'hidden'
        }}>
          {/* Header Row */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '6px'
          }}>
            <div style={{ 
              fontWeight: isUnread ? '700' : '600',
              color: isActive ? '#667eea' : '#2c3e50',
              fontSize: '16px',
              lineHeight: '1.3',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {userData?.firstName} {userData?.lastName}
              {isUnread && (
                <span style={{ 
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#667eea'
                }} />
              )}
              {activeTab !== 'inbox' && getMessageIcon()}
            </div>
            <Text style={{ 
              color: '#94a3b8',
              fontSize: '12px',
              flexShrink: 0,
              marginLeft: '8px'
            }}>
              {timeString}
            </Text>
          </div>

          {/* Profile Tags */}
          <div style={{ marginBottom: '8px' }}>
            <Tag size="small" color="blue" style={{ fontSize: '11px' }}>
              {userData?.age || 25} yrs
            </Tag>
            <Tag size="small" color="green" style={{ fontSize: '11px' }}>
              {userData?.currentResidence || 'Location'}
            </Tag>
            {activeTab === 'sent' && (
              <Tag size="small" color="purple" style={{ fontSize: '11px' }}>
                Sent
              </Tag>
            )}
          </div>

          {/* Message Preview */}
          <Text style={{ 
            color: isUnread ? '#2c3e50' : '#64748b',
            fontSize: '14px',
            lineHeight: '1.4',
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: isUnread ? '500' : 'normal'
          }}>
            {getMessagePreview()}
          </Text>

          {/* Status indicator for sent messages */}
          {activeTab === 'sent' && lastMessage && (
            <Text style={{ 
              fontSize: '12px',
              color: '#94a3b8',
              fontStyle: 'italic',
              marginTop: '4px',
              display: 'block'
            }}>
              {lastMessage.read ? 'Read' : 'Delivered'}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationPreview;